import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  makeStyles,
  Tooltip,
  Button,
  Grid,
  Typography,
  DialogTitle,
  Divider,
  TextField,
  FormControl,
  Box,
  IconButton,
  Chip,
  Alert,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { signInVehicle, signOutVehicle, getVehicleSignsByUser, getVehicleSigns } from "../../../actions/vehicleAction";
import { getFleetByLocation } from "../../../actions/vehicleAction";
import Feedback from "../../../utils/Feedback";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 600,
    [theme.breakpoints.down("sm")]: {
      minWidth: 400,
    },
    [theme.breakpoints.down("xs")]: {
      minWidth: "100%",
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  btn: {
    marginTop: 30,
    marginBottom: 30,
  },
  formControl: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minWidth: "100%",
  },
  vehicleStatusBox: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
  },
  statusChipIn: {
    backgroundColor: "#eb3349",
    color: "#fff",
    fontWeight: 700,
  },
  statusChipOut: {
    backgroundColor: "#56ab2f",
    color: "#fff",
    fontWeight: 700,
  },
}));

const INIT_STATE = {
  operator: "",
  vehicle: null,
  signType: null,
  jobNumber: "",
  jobSite: "",
};

const CreateVehicleSignModal = ({ openCreate, handleCloseCreate }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [state, setState] = useState(INIT_STATE);
  const [vehicleStatus, setVehicleStatus] = useState(null);
  const [feed, setFeed] = useState({
    open: false,
    message: "",
    success: false,
  });

  const { loading, error } = useSelector((state) => state.vehicle);
  const { profile } = useSelector((state) => state.users);
  const { locFleets, vehicleSigns } = useSelector((state) => state.vehicle);

  useEffect(() => {
    dispatch(getFleetByLocation(profile.location?._id));
    dispatch(getVehicleSigns());
  }, [profile, dispatch]);

  //filter and sort alphabetically

  const sortedVehicles = [...locFleets].filter(fleet => fleet.location !== undefined).sort((a, b) => {
    return a.displayname.localeCompare(b.displayname);
  });

  // Determine vehicle status when a vehicle is selected
  useEffect(() => {
    if (state.vehicle && vehicleSigns) {
      const vehicleSignRecords = vehicleSigns.filter(
        (sign) => sign.vehicle && sign.vehicle._id === state.vehicle._id
      );

      if (vehicleSignRecords.length === 0) {
        // No sign records, vehicle is available for sign out
        setVehicleStatus({
          canSignOut: true,
          canSignIn: false,
          lastAction: null,
        });
        setState((prev) => ({
          ...prev,
          signType: "Sign Out",
          jobSite: profile.location?.displayname || "",
        }));
        return;
      }

      // Get the most recent sign
      const latestSign = vehicleSignRecords.sort((a, b) => {
        const dateA = a.signType === "Sign In" ? new Date(a.signedInAt) : new Date(a.signedOutAt);
        const dateB = b.signType === "Sign In" ? new Date(b.signedInAt) : new Date(b.signedOutAt);
        return dateB - dateA;
      })[0];

      if (latestSign.signType === "Sign Out") {
        // Last action was sign out, so vehicle is currently in use - can only sign in
        setVehicleStatus({
          canSignOut: false,
          canSignIn: true,
          lastAction: "Sign Out",
          lastOperator: latestSign.operator,
        });
        setState((prev) => ({
          ...prev,
          signType: "Sign In",
          jobSite: profile.location?.displayname || "",
          jobNumber: "",
        }));
      } else {
        // Last action was sign in, so vehicle is available - can only sign out
        setVehicleStatus({
          canSignOut: true,
          canSignIn: false,
          lastAction: "Sign In",
          lastOperator: latestSign.operator,
        });
        setState((prev) => ({
          ...prev,
          signType: "Sign Out",
          jobSite: "",
        }));
      }
    } else {
      setVehicleStatus(null);
      setState((prev) => ({
        ...prev,
        signType: null,
      }));
    }
  }, [state.vehicle, vehicleSigns, profile]);

  const handleChange = (e) => {
    e.persist();
    setState((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
  };

  const handleVehicleChange = (event, value) => {
    setState((prevState) => ({ 
      ...prevState, 
      vehicle: value,
      jobNumber: "",
      jobSite: "",
    }));
  };

  const isValid = () => {
    return !state.operator || !state.vehicle || !state.signType;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      operator: state.operator,
      vehicle: state.vehicle._id,
      signType: state.signType,
      ...(state.signType === "Sign Out" && state.jobNumber && { jobNumber: state.jobNumber }),
      ...(state.jobSite && { jobSite: state.jobSite }),
    };

    try {
      if (state.signType === "Sign In") {
        await dispatch(signInVehicle(data));
      } else {
        await dispatch(signOutVehicle(data));
      }

      handleCloseCreate();
      dispatch(getVehicleSignsByUser());
      dispatch(getVehicleSigns());
      setState(INIT_STATE);
      setVehicleStatus(null);
      setFeed({
        open: true,
        message: `Vehicle ${state.signType === "Sign In" ? "signed in" : "signed out"} successfully.`,
        success: true,
      });
    } catch (err) {
      setFeed({
        open: true,
        message: err.message || "Failed to complete vehicle sign action.",
        success: false,
      });
    }
  };

  const handleCloseFeed = () => {
    setFeed((prev) => ({ ...prev, open: false }));
  };

  const handleClose = () => {
    handleCloseCreate();
    setState(INIT_STATE);
    setVehicleStatus(null);
  };

  return (
    <>
    <Feedback
      handleCloseFeed={handleCloseFeed}
      open={feed.open}
      severity={feed.success ? "success" : "error"}
      message={feed.message}
    />
    <Dialog
      open={openCreate}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="overline" className={classes.title}>
              Vehicle Sign In/Out
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip arrow title="Close">
              <IconButton onClick={handleClose} size="large">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />
      
      <DialogContent className={classes.dialogContent}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="operator"
                fullWidth
                variant="outlined"
                label="Operator Name"
                className={classes.textField}
                onChange={handleChange}
                value={state.operator || ""}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl className={classes.formControl}>
                <Autocomplete 
                  id="vehicle"
                  options={sortedVehicles || []}
                  getOptionSelected={(option, value) => option._id === value._id}
                  getOptionLabel={(option) => `${option.displayname || option.name} - ${option.unitNumber || option.licencePlate || 'No Unit #'}`} 
                  onChange={handleVehicleChange}
                  value={state.vehicle || null}
                  renderInput={(params) => 
                    <TextField {...params} variant="outlined" label="Select Vehicle" required />
                  }
                />
              </FormControl>
            </Grid>

            {/* Vehicle Status Display */}
            {vehicleStatus && (
              <Grid item xs={12}>
                <Box className={classes.vehicleStatusBox}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <InfoIcon color="primary" />
                    </Grid>
                    <Grid item xs>
                      {vehicleStatus.canSignOut && (
                        <>
                          <Typography variant="body2" gutterBottom>
                            <strong>Vehicle Status:</strong> Available
                          </Typography>
                          <Chip
                            label="You can ONLY SIGN OUT this vehicle"
                            size="small"
                            className={classes.statusChipOut}
                          />
                          {vehicleStatus.lastOperator && (
                            <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                              Last signed in by: {vehicleStatus.lastOperator}
                            </Typography>
                          )}
                        </>
                      )}
                      {vehicleStatus.canSignIn && (
                        <>
                          <Typography variant="body2" gutterBottom>
                            <strong>Vehicle Status:</strong> Currently In Use
                          </Typography>
                          <Chip
                            label="You can ONLY SIGN IN this vehicle"
                            size="small"
                            className={classes.statusChipIn}
                          />
                          {vehicleStatus.lastOperator && (
                            <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                              Last signed out by: {vehicleStatus.lastOperator}
                            </Typography>
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            {state.signType === "Sign In" && state.vehicle && (
              <Grid item xs={12}>
                <TextField
                  name="jobSite"
                  fullWidth
                  variant="outlined"
                  label="Pickup Location"
                  className={classes.textField}
                  value={profile.location?.displayname || ""}
                  disabled
                  helperText="Vehicle is being returned to your location"
                />
              </Grid>
            )}

            {state.signType === "Sign Out" && state.vehicle && (
              <>
                <Grid item xs={12}>
                  <TextField
                    name="jobNumber"
                    fullWidth
                    variant="outlined"
                    label="Job Number"
                    className={classes.textField}
                    onChange={handleChange}
                    value={state.jobNumber || ""}
                    helperText="Enter job number if applicable"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="jobSite"
                    fullWidth
                    variant="outlined"
                    label="Job Site / Destination"
                    className={classes.textField}
                    onChange={handleChange}
                    value={state.jobSite || ""}
                    helperText="Enter where you're taking the vehicle"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                type="submit"
                className={classes.btn}
                disabled={isValid() || loading}
              >
                {loading 
                  ? "Processing..." 
                  : state.signType 
                    ? `${state.signType === "Sign In" ? "Sign In" : "Sign Out"} Vehicle`
                    : "Select a Vehicle"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CreateVehicleSignModal;
